import type React from 'react';
import { useLocation } from 'react-router-dom';
import { Breadcrumbs, type BreadcrumbItem } from './Breadcrumbs.js';
import { PageTitle, type PageHelpContent } from './PageTitle.js';
import { useInEntityPanel } from '../page/entity-panel-scope.js';

export type { PageHelpContent };

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  badges?: React.ReactNode;
  helpContent?: PageHelpContent;
  actions?: React.ReactNode;
  /**
   * Optional sub-row rendered beneath the title/crumb row (e.g. a project
   * summary-counts strip). Renders nothing when absent, so non-project pages
   * are visually unchanged.
   */
  meta?: React.ReactNode;
}

function defaultBreadcrumbs(pathname: string, title: string): BreadcrumbItem[] {
  const rules: Array<{ prefix: string; items: BreadcrumbItem[] }> = [
    {
      prefix: '/agents/libraries/catalog-review',
      items: [
        { label: 'AI Agents' },
        { label: 'Libraries', to: '/agents/libraries' },
        { label: 'Catalog Review', to: '/agents/libraries/catalog-review' },
      ],
    },
    {
      prefix: '/agents/libraries/models',
      items: [
        { label: 'AI Agents' },
        { label: 'Libraries', to: '/agents/libraries' },
        { label: 'Models', to: '/agents/libraries/models' },
      ],
    },
    {
      prefix: '/agents/libraries/model-runtime-policies',
      items: [
        { label: 'AI Agents' },
        { label: 'Libraries', to: '/agents/libraries' },
        { label: 'Model Runtime Policies', to: '/agents/libraries/model-runtime-policies' },
      ],
    },
    { prefix: '/agents/libraries', items: [{ label: 'AI Agents' }, { label: 'Libraries', to: '/agents/libraries' }] },
    {
      prefix: '/agents/templates',
      items: [{ label: 'AI Agents' }, { label: 'Agent Templates', to: '/agents/templates' }],
    },
    { prefix: '/agents/tools', items: [{ label: 'AI Agents' }, { label: 'Tools', to: '/agents/tools' }] },
    { prefix: '/agents/workflows', items: [{ label: 'AI Agents' }, { label: 'Workflows', to: '/agents/workflows' }] },
    { prefix: '/agents/runs', items: [{ label: 'AI Agents' }, { label: 'Runs', to: '/agents/runs' }] },
    { prefix: '/agents/decisions', items: [{ label: 'AI Agents' }, { label: 'Decisions', to: '/agents/decisions' }] },
    {
      prefix: '/agents/intel-sources',
      items: [{ label: 'Knowledge' }, { label: 'Intel Sources', to: '/agents/intel-sources' }],
    },
    { prefix: '/conversations', items: [{ label: 'AI Agents' }, { label: 'Conversations', to: '/conversations' }] },
    { prefix: '/projects', items: [{ label: 'Projects', to: '/projects' }] },
    { prefix: '/tasks', items: [{ label: 'Tasks', to: '/tasks' }] },
    { prefix: '/memories', items: [{ label: 'Knowledge' }, { label: 'Memories', to: '/memories' }] },
    { prefix: '/artifacts', items: [{ label: 'Knowledge' }, { label: 'Artifacts', to: '/artifacts' }] },
    { prefix: '/marketplace', items: [{ label: 'Marketplace', to: '/marketplace' }] },
    { prefix: '/settings', items: [{ label: 'Settings', to: '/settings' }] },
    { prefix: '/admin/daemons', items: [{ label: 'Admin' }, { label: 'Daemons', to: '/admin/daemons' }] },
    { prefix: '/admin', items: [{ label: 'Admin', to: '/admin' }] },
  ];

  const match = rules.find((rule) => pathname === rule.prefix || pathname.startsWith(`${rule.prefix}/`));
  if (!match) return [];
  return match.items.filter((item) => item.label !== title);
}

/**
 * The page header. The title (with its help icon and badges) is a `PageTitle`, which sits in the shell header on wide
 * screens; the page itself starts with one row: chevron breadcrumbs (Home > section > page) on the left and the page
 * actions on the right (owner 2026-10-17). Template example: NextAdmin `(with-layouts)/tables/basic-tables/page.tsx`
 * header (chevron breadcrumbs). Every routed page renders exactly one — through its page template.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  breadcrumbs,
  badges,
  helpContent,
  actions,
  meta,
}) => {
  const location = useLocation();
  // In a side panel the trail would lead out of the page the panel is on.
  const inPanel = useInEntityPanel();
  const resolvedBreadcrumbs = (breadcrumbs ?? defaultBreadcrumbs(location.pathname, title)).filter(
    (item) => item.label !== title,
  );
  // The template trail is Home > section > current page.
  const crumbs: BreadcrumbItem[] = [{ label: 'Home', to: '/' }, ...resolvedBreadcrumbs, { label: title }];

  return (
    <div className="flex flex-col gap-3">
      <PageTitle title={title} description={subtitle} helpContent={helpContent} badges={badges} />
      {(!inPanel || actions) && (
        <header className="flex min-h-10 flex-wrap items-center justify-between gap-3">
          {!inPanel && <Breadcrumbs items={crumbs} />}
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </header>
      )}
      {meta && <div>{meta}</div>}
    </div>
  );
};
